import {
    IOpenRegistrationOptions,
    IRegistrationExternalLinkHandler, REGISTRATION_DATA_KEY
} from "./registration.external-link-handler.interface";
import {ExternalLinksZIndexes} from "../../external-links-z-indexes";
import {CHANNEL_ELEMENTS, CHANNEL_SOURCE} from "../../../../../common/const/casinofied-analytics-consts";
import {AreaType} from "@sparkware/uc-sdk-core";
import {NullableUndefinedString} from "../../../../../common/types/nullable-types";
import {GNWP_SOURCES} from "../../../gnwp/gnwp-consts";
import {CIPropRead} from "../../../../../utils/case-insensitive-property-reader";
import Store from "../../../../../sdk/src/utils/store";
import {
    ExternalLinkInIframeHandlerBase,
    ExternalLinkInIframeRendererParams
} from "../external-link-in-iframe-handler-base";
import {CyClass} from "../../../../../common/cy-classes";



const MEMBERSHIP_POST_ACTIONS = {
    PLAY: '1',
    DEPOSIT: '2',
    BACK: '3',
    CASHIER_UPLOAD_DOCUMENTS: '7',
    REGISTRATION_COMPLETED: '8',
    OPEN_BANK_ID: '64',
    DOWNLOAD_APP: '68',
    STORE_REGISTRATION_COOKIES_DATA:'69',
    SHOW_LOGIN: '73',
    MY_ACCOUNT_UPLOAD_DOCUMENTS: '75',
    REDIRECT_CLIENT : '77'
};

export class RegistrationExternalLinkHandler extends ExternalLinkInIframeHandlerBase implements IRegistrationExternalLinkHandler {

    protected _getGnwpSource(): GNWP_SOURCES {
        return GNWP_SOURCES.MEMBERSHIP;
    }

    async openRegistration(options?: IOpenRegistrationOptions): Promise<void> {
        if(this.services.user.isAuthorized) {
            throw new Error('openRegistration should not be called for authorized user');
        }

        const url = await this._getGnwpUrl(options);
        await this._showExternalLinkDialog(url);
    }


    protected async _getGnwpUrl(options: IOpenRegistrationOptions): Promise<string> {
        let marketingData = this.services.marketing.getMarketingData(options.marketingParams);
        return await this.services.gnwp.getRegistrationUrl({
            marketingData: marketingData
        });
    }

    protected _getIframeRendererParams(): ExternalLinkInIframeRendererParams {
        return {
            maxWidth: 1008,
            maxHeight: 730,
            zIndex: ExternalLinksZIndexes.Membership,
            shouldBeClosedOnlyByUser: true,
            hideCloseButton: this._shouldHideCloseButton,
            httpVerb: 'POST',
            analyticsAction: this._getGnwpSource(),
            iframeExtraCssClass: CyClass('cy-registration-external-link-container')
        };
    }

    protected get _shouldHideCloseButton(): boolean {
        return false;
    }

    protected async _onPostMessageReceived(message: MessageEvent): Promise<void> {
        const actionId = this._extractActionId(message);
        if(!actionId) {
            return;
        }

        switch (actionId) {
            case MEMBERSHIP_POST_ACTIONS.PLAY:
            case MEMBERSHIP_POST_ACTIONS.BACK:
                this._closeExternalLinkDialog();
                break;
            case MEMBERSHIP_POST_ACTIONS.DEPOSIT:
                await this._openCashier({
                    tabId: undefined,
                    promCode: this._extractPromoCode(message),
                    channel: {
                        source: CHANNEL_SOURCE.registration,
                        area: AreaType.StraightToCashier,
                        element: CHANNEL_ELEMENTS.depositButton
                    }
                });
                break;
            case MEMBERSHIP_POST_ACTIONS.CASHIER_UPLOAD_DOCUMENTS:
                await this._openUploadDocumentsFromCashier();
                break;
            case MEMBERSHIP_POST_ACTIONS.SHOW_LOGIN:
                await this._openLoginForm();
                break;
            case MEMBERSHIP_POST_ACTIONS.REGISTRATION_COMPLETED:
                const messageData = this._parseMessageData(message);
                await this._onRegistrationCompleted({
                    UserInfo: CIPropRead.getStringProperty('UserInfo', messageData),
                    SourceProductPackage: CIPropRead.getStringProperty('SourceProductPackage', messageData)
                });
                break;
            case MEMBERSHIP_POST_ACTIONS.STORE_REGISTRATION_COOKIES_DATA:
                const registrationCookiesData = CIPropRead.getProperty('StoreRegistrationCookiesData', this._parseMessageData(message));
                if(registrationCookiesData) {
                    await this._storeRegistrationCookie(registrationCookiesData);
                }
                break;
            case MEMBERSHIP_POST_ACTIONS.MY_ACCOUNT_UPLOAD_DOCUMENTS:
                await this._openUploadDocumentsFromMyAccount();
                break;
            case MEMBERSHIP_POST_ACTIONS.OPEN_BANK_ID:
                const url = CIPropRead.getStringProperty('AppUrl', this._parseMessageData(message));
                if(url) {
                    await this._openBankId(url);
                } else {
                    this.services.logger.error('Registration try to open bank ID with empty URL');
                }
                break;
            case MEMBERSHIP_POST_ACTIONS.DOWNLOAD_APP:
                await this._openDownloadAppPage();
                break;
            case MEMBERSHIP_POST_ACTIONS.REDIRECT_CLIENT:
                await this._redirectToURL(message)
                break;
            default:
                this.services.logger.debug(`Received unknown actionId ${actionId} from Registration`);
        }
    }

    protected async _openLoginForm(): Promise<void> {
        this._closeExternalLinkDialog();
        await this.services.user.openLogin();
    }

    protected async _onRegistrationCompleted(params: {UserInfo: string, SourceProductPackage: string}): Promise<void> {
        this.services.sdk.eventsTrigger.registrationCompleted(params);
    }

    protected async _openUploadDocumentsFromCashier(): Promise<void> {
        this._closeExternalLinkDialog();
        await this.services.cashier.openUploadDocumentsFromCashier();
    }

    protected async _openUploadDocumentsFromMyAccount(): Promise<void> {
        this._closeExternalLinkDialog();
        await this.services.externalLinks.openUploadDocumentFromMyAccount();
    }

    protected async _openBankId(url: NullableUndefinedString): Promise<void> {
        this.services.externalLinks.openBankId(url)
    }

    protected async _openDownloadAppPage(): Promise<void> {
        await this.services.location.redirect(this.services.sdk.getDownloadAppLink());
    }

    protected async _storeRegistrationCookie(registrationCookiesData: any): Promise<void> {
        Store.set(REGISTRATION_DATA_KEY, JSON.stringify(registrationCookiesData));
    }



}